/**
 * API Client
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'user';
  permissions: string[];
  dashboardAccess: string[];
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLogEntry {
  id: string;
  userId: string | null;
  userEmail?: string | null;
  userName?: string | null;
  action: string;
  tableName: string;
  recordId: string | null;
  oldValues: string | null;
  newValues: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface LoginEvent {
  id: string;
  userId: string | null;
  email: string;
  userName: string | null;
  eventType: 'login_success' | 'login_failed' | 'logout' | 'token_refresh';
  failureReason: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface LoginActivityRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  logins24h: number;
  logins7d: number;
  failures24h: number;
  activeSessions: number;
}

export interface OnlineUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  lastLoginAt: string | null;
  mostRecentSessionAt: string;
  activeSessionCount: number;
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // Load tokens from localStorage
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  getAccessToken() {
    return this.accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_URL}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response: Response;
    try {
      response = await fetch(url, { ...options, headers });
    } catch {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: 'Network error occurred' },
      };
    }

    // Handle 401 - try to refresh token
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
        try {
          const retryResponse = await fetch(url, { ...options, headers });
          return await retryResponse.json();
        } catch {
          return {
            success: false,
            error: { code: 'NETWORK_ERROR', message: 'Network error occurred' },
          };
        }
      }
      // Refresh failed — redirect to login
      window.location.href = '/login';
      return {
        success: false,
        error: { code: 'SESSION_EXPIRED', message: 'Session expired. Please log in again.' },
      };
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.error?.message || `Request failed (${response.status})`);
    }

    if (response.status === 204) {
      return { success: true } as ApiResponse<T>;
    }

    return await response.json();
  }

  async tryRefreshToken(): Promise<boolean> {
    if (!this.refreshToken) return false;

    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.accessToken) {
          this.accessToken = data.data.accessToken;
          localStorage.setItem('accessToken', data.data.accessToken);
          return true;
        }
      }
    } catch {
      // Refresh failed
    }

    this.clearTokens();
    return false;
  }

  // File upload (FormData, no Content-Type header - browser sets multipart boundary)
  private async uploadRequest<T>(
    endpoint: string,
    formData: FormData
  ): Promise<ApiResponse<T>> {
    const url = `${API_URL}${endpoint}`;
    const headers: Record<string, string> = {};

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (response.status === 401 && this.refreshToken) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          headers['Authorization'] = `Bearer ${this.accessToken}`;
          const retryResponse = await fetch(url, { method: 'POST', headers, body: formData });
          return retryResponse.json();
        }
      }

      return response.json();
    } catch {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: 'Network error occurred' },
      };
    }
  }

  async uploadLogo(file: File): Promise<ApiResponse<{ logoUrl: string }>> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.uploadRequest<{ logoUrl: string }>('/uploads/logo', formData);
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request<{
      user: { id: string; email: string; firstName: string; lastName: string; role: string; dashboardAccess: string[] };
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(data: { email: string; password: string; firstName: string; lastName: string }) {
    return this.request<{
      user: { id: string; email: string; firstName: string; lastName: string; role: string; dashboardAccess: string[] };
      tokens: { accessToken: string; refreshToken: string; expiresIn: number };
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async logout() {
    const result = await this.request('/auth/logout', { method: 'POST' });
    this.clearTokens();
    return result;
  }

  async getSsoEnabled() {
    return this.request<{ enabled: boolean }>('/auth/sso/enabled');
  }

  getSsoLoginUrl() {
    return `${API_URL}/auth/sso/login`;
  }

  async getMe() {
    return this.request<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      dashboardAccess: string[];
      permissions: string[];
    }>('/auth/me');
  }

  // Accounts
  async getAccounts(params?: { page?: number; limit?: number; status?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);

    return this.request<unknown[]>(`/accounts?${searchParams}`);
  }

  async getAccount(id: string) {
    return this.request(`/accounts/${id}`);
  }

  async createAccount(data: { name: string; industry?: string; website?: string; phone?: string }) {
    return this.request('/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAccount(id: string, data: Record<string, unknown>) {
    return this.request(`/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAccount(id: string) {
    return this.request(`/accounts/${id}`, { method: 'DELETE' });
  }

  // Contacts
  async getContacts(params?: { accountId?: string; page?: number; limit?: number; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);

    return this.request<unknown[]>(`/contacts?${searchParams}`);
  }

  async createContact(data: { accountId: string; email: string; firstName: string; lastName: string; phone?: string; jobTitle?: string }) {
    return this.request('/contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: Record<string, unknown>) {
    return this.request(`/contacts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string) {
    return this.request(`/contacts/${id}`, { method: 'DELETE' });
  }

  // Deals
  async getDeals(params?: { page?: number; limit?: number; stage?: string; accountId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.stage) searchParams.set('stage', params.stage);
    if (params?.accountId) searchParams.set('accountId', params.accountId);

    return this.request<unknown[]>(`/deals?${searchParams}`);
  }

  async getPipelineSummary() {
    return this.request<{ stage: string; dealCount: number; totalValue: number; weightedValue: number }[]>('/deals/pipeline');
  }

  async createDeal(data: { accountId: string; title: string; value?: number; stage?: string }) {
    return this.request('/deals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDeal(id: string, data: Record<string, unknown>) {
    return this.request(`/deals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteDeal(id: string) {
    return this.request(`/deals/${id}`, { method: 'DELETE' });
  }

  // Tickets
  async getTickets(params?: { page?: number; limit?: number; status?: string; priority?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.priority) searchParams.set('priority', params.priority);
    if (params?.search) searchParams.set('search', params.search);

    return this.request<unknown[]>(`/tickets?${searchParams}`);
  }

  async getTicket(id: string) {
    return this.request(`/tickets/${id}`);
  }

  async createTicket(data: { accountId: string; subject: string; description?: string; priority?: string }) {
    return this.request('/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTicket(id: string, data: Record<string, unknown>) {
    return this.request(`/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getTicketMessages(ticketId: string) {
    return this.request<unknown[]>(`/tickets/${ticketId}/messages`);
  }

  async addTicketMessage(ticketId: string, content: string, isInternal = false) {
    return this.request(`/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, isInternal }),
    });
  }
  // ============================================
  // Finance: Business Profiles
  // ============================================

  async getBusinessProfiles() {
    return this.request<unknown[]>('/business-profiles');
  }

  async getBusinessProfile(id: string) {
    return this.request(`/business-profiles/${id}`);
  }

  async createBusinessProfile(data: Record<string, unknown>) {
    return this.request('/business-profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBusinessProfile(id: string, data: Record<string, unknown>) {
    return this.request(`/business-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBusinessProfile(id: string) {
    return this.request(`/business-profiles/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Applications
  // ============================================

  async getFinanceApplications(params?: { businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/finance-applications?${searchParams}`);
  }

  async getFinanceApplication(id: string) {
    return this.request(`/finance-applications/${id}`);
  }

  async createFinanceApplication(data: Record<string, unknown>) {
    return this.request('/finance-applications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFinanceApplication(id: string, data: Record<string, unknown>) {
    return this.request(`/finance-applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFinanceApplication(id: string) {
    return this.request(`/finance-applications/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Invoices
  // ============================================

  async getInvoices(params?: { page?: number; limit?: number; status?: string; businessProfileId?: string; accountId?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/invoices?${searchParams}`);
  }

  async getInvoice(id: string) {
    return this.request(`/invoices/${id}`);
  }

  async createInvoice(data: Record<string, unknown>) {
    return this.request('/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInvoice(id: string, data: Record<string, unknown>) {
    return this.request(`/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateInvoiceStatus(id: string, status: string) {
    return this.request(`/invoices/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async deleteInvoice(id: string) {
    return this.request(`/invoices/${id}`, { method: 'DELETE' });
  }

  async duplicateInvoice(id: string) {
    return this.request(`/invoices/${id}/duplicate`, { method: 'POST' });
  }

  async getRevenueByProfile() {
    return this.request<unknown[]>('/invoices/revenue-by-profile');
  }

  async getInvoiceSummary() {
    return this.request<{ summary: unknown; recentInvoices: unknown[] }>('/invoices/summary');
  }

  async getInvoiceAccounts(search?: string) {
    const searchParams = new URLSearchParams();
    if (search) searchParams.set('search', search);
    return this.request<{ id: string; name: string }[]>(`/invoices/accounts?${searchParams}`);
  }

  // ============================================
  // Finance: Payments
  // ============================================

  async getPayments(params?: { page?: number; limit?: number; invoiceId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.invoiceId) searchParams.set('invoiceId', params.invoiceId);
    return this.request<unknown[]>(`/payments?${searchParams}`);
  }

  async createPayment(data: Record<string, unknown>) {
    return this.request('/payments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deletePayment(id: string) {
    return this.request(`/payments/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Other Income
  // ============================================

  async getOtherIncome(params?: { page?: number; limit?: number; incomeType?: string; businessProfileId?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.incomeType) searchParams.set('incomeType', params.incomeType);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/other-income?${searchParams}`);
  }

  async createOtherIncome(data: Record<string, unknown>) {
    return this.request('/other-income', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOtherIncome(id: string, data: Record<string, unknown>) {
    return this.request(`/other-income/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOtherIncome(id: string) {
    return this.request(`/other-income/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Contracts
  // ============================================

  async getContracts(params?: { page?: number; limit?: number; accountId?: string; status?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/contracts?${searchParams}`);
  }

  async getContract(id: string) {
    return this.request(`/contracts/${id}`);
  }

  async createContract(data: Record<string, unknown>) {
    return this.request('/contracts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContract(id: string, data: Record<string, unknown>) {
    return this.request(`/contracts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteContract(id: string) {
    return this.request(`/contracts/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Recurring Schedules
  // ============================================

  async getRecurringSchedules(params?: { page?: number; limit?: number; accountId?: string; contractId?: string; status?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.contractId) searchParams.set('contractId', params.contractId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/recurring-schedules?${searchParams}`);
  }

  async getRecurringSchedule(id: string) {
    return this.request(`/recurring-schedules/${id}`);
  }

  async createRecurringSchedule(data: Record<string, unknown>) {
    return this.request('/recurring-schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRecurringSchedule(id: string, data: Record<string, unknown>) {
    return this.request(`/recurring-schedules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteRecurringSchedule(id: string) {
    return this.request(`/recurring-schedules/${id}`, { method: 'DELETE' });
  }

  async getDueSchedules(targetDate?: string) {
    const searchParams = new URLSearchParams();
    if (targetDate) searchParams.set('targetDate', targetDate);
    return this.request<unknown[]>(`/recurring-schedules/due?${searchParams}`);
  }

  async generateInvoice(scheduleId: string) {
    return this.request<{ invoiceId: string }>(`/recurring-schedules/${scheduleId}/generate`, {
      method: 'POST',
    });
  }

  async generateBatch(scheduleIds: string[]) {
    return this.request<{ scheduleId: string; invoiceId: string | null; success: boolean }[]>(
      '/recurring-schedules/generate-batch',
      {
        method: 'POST',
        body: JSON.stringify({ scheduleIds }),
      }
    );
  }

  // ============================================
  // Finance: Billing Alerts
  // ============================================

  async getAlerts(params?: { page?: number; limit?: number; alertType?: string; isRead?: boolean }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.alertType) searchParams.set('alertType', params.alertType);
    if (params?.isRead !== undefined) searchParams.set('isRead', String(params.isRead));
    return this.request<unknown[]>(`/billing-alerts?${searchParams}`);
  }

  async getAlertCounts() {
    return this.request<{ totalUnread: number; criticalCount: number; warningCount: number; infoCount: number }>(
      '/billing-alerts/counts'
    );
  }

  async markAlertRead(id: string) {
    return this.request(`/billing-alerts/${id}/read`, { method: 'PATCH' });
  }

  async dismissAlert(id: string) {
    return this.request(`/billing-alerts/${id}/dismiss`, { method: 'PATCH' });
  }

  // ============================================
  // Finance: Email
  // ============================================

  async sendInvoiceEmail(invoiceId: string) {
    return this.request(`/email/send-invoice/${invoiceId}`, { method: 'POST' });
  }

  async sendPaymentReminder(invoiceId: string) {
    return this.request(`/email/send-reminder/${invoiceId}`, { method: 'POST' });
  }

  async getEmailLog(params?: { entityType?: string; entityId?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.entityType) searchParams.set('entityType', params.entityType);
    if (params?.entityId) searchParams.set('entityId', params.entityId);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/email/log?${searchParams}`);
  }
  // ============================================
  // Finance: Expenses
  // ============================================

  async getExpenseCategories() {
    return this.request<unknown[]>('/expenses/categories');
  }

  async createExpenseCategory(data: { name: string; slug: string; description?: string; icon?: string }) {
    return this.request('/expenses/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteExpenseCategory(id: string) {
    return this.request(`/expenses/categories/${id}`, { method: 'DELETE' });
  }

  async getExpenseSummary(params?: { dateFrom?: string; dateTo?: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/expenses/summary?${searchParams}`);
  }

  async getExpenses(params?: {
    page?: number; limit?: number; categoryId?: string;
    businessProfileId?: string; dateFrom?: string; dateTo?: string;
    search?: string; isTaxDeductible?: boolean;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.isTaxDeductible !== undefined) searchParams.set('isTaxDeductible', String(params.isTaxDeductible));
    return this.request<unknown[]>(`/expenses?${searchParams}`);
  }

  async createExpense(data: Record<string, unknown>) {
    return this.request('/expenses', { method: 'POST', body: JSON.stringify(data) });
  }

  async importExpenses(expenses: Array<Record<string, unknown>>) {
    return this.request<{
      created: number;
      failed: number;
      total: number;
      errors: Array<{ row: number; error: string }>;
      expenses: unknown[];
    }>('/expenses/import', {
      method: 'POST',
      body: JSON.stringify({ expenses }),
    });
  }

  async updateExpense(id: string, data: Record<string, unknown>) {
    return this.request(`/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteExpense(id: string) {
    return this.request(`/expenses/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Subscriptions
  // ============================================

  async getSubscriptionOverview(params?: { businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/subscriptions/overview?${searchParams}`);
  }

  async getSubscriptions(params?: {
    page?: number; limit?: number; billingCycle?: string;
    status?: string; businessProfileId?: string; search?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.billingCycle) searchParams.set('billingCycle', params.billingCycle);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/subscriptions?${searchParams}`);
  }

  async createSubscription(data: Record<string, unknown>) {
    return this.request('/subscriptions', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateSubscription(id: string, data: Record<string, unknown>) {
    return this.request(`/subscriptions/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteSubscription(id: string) {
    return this.request(`/subscriptions/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Loans & Mortgages
  // ============================================

  async getLoanOverview(params?: { businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/loans/overview?${searchParams}`);
  }

  async getLoans(params?: {
    page?: number; limit?: number; loanType?: string;
    status?: string; businessProfileId?: string; search?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.loanType) searchParams.set('loanType', params.loanType);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/loans?${searchParams}`);
  }

  async createLoan(data: Record<string, unknown>) {
    return this.request('/loans', { method: 'POST', body: JSON.stringify(data) });
  }

  async getLoan(id: string) {
    return this.request(`/loans/${id}`);
  }

  async getLoanAmortization(id: string) {
    return this.request<unknown[]>(`/loans/${id}/amortization`);
  }

  async updateLoan(id: string, data: Record<string, unknown>) {
    return this.request(`/loans/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteLoan(id: string) {
    return this.request(`/loans/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Charge Accounts
  // ============================================

  async getChargeAccountOverview(params?: { businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/charge-accounts/overview?${searchParams}`);
  }

  async getUpcomingPayments(params?: { weekStart?: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.weekStart) searchParams.set('weekStart', params.weekStart);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/charge-accounts/upcoming-payments?${searchParams}`);
  }

  async getChargeAccounts(params?: {
    page?: number; limit?: number; accountType?: string;
    status?: string; businessProfileId?: string; search?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.accountType) searchParams.set('accountType', params.accountType);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/charge-accounts?${searchParams}`);
  }

  async createChargeAccount(data: Record<string, unknown>) {
    return this.request('/charge-accounts', { method: 'POST', body: JSON.stringify(data) });
  }

  async getChargeAccount(id: string) {
    return this.request(`/charge-accounts/${id}`);
  }

  async getChargeAccountAllocations(id: string) {
    return this.request<{
      allocations: Array<{
        id: string;
        kind: 'subscription' | 'expense';
        name: string;
        vendor: string | null;
        categoryName: string | null;
        amount: number;
        frequency: string | null;
        paymentDueDay: number | null;
        monthlyCost: number;
      }>;
      summary: { totalMonthlyAllocated: number; allocationCount: number };
    }>(`/charge-accounts/${id}/allocations`);
  }

  async updateChargeAccount(id: string, data: Record<string, unknown>) {
    return this.request(`/charge-accounts/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteChargeAccount(id: string) {
    return this.request(`/charge-accounts/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Payment Schedule / Bill Pay
  // ============================================

  async getPaymentSchedule(params?: { startDate?: string; weeks?: number; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.weeks) searchParams.set('weeks', params.weeks.toString());
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/payment-schedule?${searchParams}`);
  }

  async markPaymentPaid(data: { source: string; sourceId: string; dueDate: string; paidAmount?: number; notes?: string }) {
    return this.request('/payment-schedule/mark-paid', { method: 'POST', body: JSON.stringify(data) });
  }

  async unmarkPayment(data: { source: string; sourceId: string; dueDate: string }) {
    return this.request('/payment-schedule/unmark', { method: 'POST', body: JSON.stringify(data) });
  }

  async slidePayment(data: { source: string; sourceId: string; dueDate: string; newScheduledDate: string; notes?: string }) {
    return this.request('/payment-schedule/slide', { method: 'POST', body: JSON.stringify(data) });
  }

  // Finance: Assets & Depreciation
  // ============================================

  async getAssetSummary(params?: { businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/assets/summary?${searchParams}`);
  }

  async getAssets(params?: {
    page?: number; limit?: number; assetType?: string;
    status?: string; businessProfileId?: string; search?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.assetType) searchParams.set('assetType', params.assetType);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    if (params?.search) searchParams.set('search', params.search);
    return this.request<unknown[]>(`/assets?${searchParams}`);
  }

  async createAsset(data: Record<string, unknown>) {
    return this.request('/assets', { method: 'POST', body: JSON.stringify(data) });
  }

  async getAsset(id: string) {
    return this.request(`/assets/${id}`);
  }

  async getAssetDepreciation(id: string) {
    return this.request<unknown[]>(`/assets/${id}/depreciation`);
  }

  async updateAsset(id: string, data: Record<string, unknown>) {
    return this.request(`/assets/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteAsset(id: string) {
    return this.request(`/assets/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Finance: Budgets
  // ============================================

  async getBudgetVsActual(params: { periodType: string; periodStart: string; periodEnd: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    searchParams.set('periodType', params.periodType);
    searchParams.set('periodStart', params.periodStart);
    searchParams.set('periodEnd', params.periodEnd);
    if (params.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/budgets/vs-actual?${searchParams}`);
  }

  async getBudgetForecast(params?: { monthsBack?: number; monthsForward?: number; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.monthsBack) searchParams.set('monthsBack', params.monthsBack.toString());
    if (params?.monthsForward) searchParams.set('monthsForward', params.monthsForward.toString());
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/budgets/forecast?${searchParams}`);
  }

  async getBudgets(params?: { page?: number; limit?: number; periodType?: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.periodType) searchParams.set('periodType', params.periodType);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/budgets?${searchParams}`);
  }

  async createBudget(data: Record<string, unknown>) {
    return this.request('/budgets', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateBudget(id: string, data: Record<string, unknown>) {
    return this.request(`/budgets/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteBudget(id: string) {
    return this.request(`/budgets/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Analytics
  // ============================================

  async getAnalyticsKpis() {
    return this.request<unknown>('/analytics/kpis');
  }

  async getSystemStatus() {
    return this.request<unknown>('/analytics/system-status');
  }

  async getApiLogs(params?: { limit?: number; status?: string; path?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.path) searchParams.set('path', params.path);
    return this.request<unknown[]>(`/analytics/api-logs?${searchParams}`);
  }

  // Admin: rate-limit activity
  async getRateLimitEvents(params?: {
    limit?: number;
    offset?: number;
    limiterName?: string;
    keyType?: string;
    search?: string;
    since?: string;
  }) {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set('limit', params.limit.toString());
    if (params?.offset) sp.set('offset', params.offset.toString());
    if (params?.limiterName) sp.set('limiterName', params.limiterName);
    if (params?.keyType) sp.set('keyType', params.keyType);
    if (params?.search) sp.set('search', params.search);
    if (params?.since) sp.set('since', params.since);
    return this.request<unknown[]>(`/admin/rate-limit/events?${sp}`);
  }

  async getRateLimitSummary() {
    return this.request<unknown[]>('/admin/rate-limit/summary');
  }

  async getApiErrors(params?: { limit?: number; path?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.path) searchParams.set('path', params.path);
    return this.request<unknown>(`/analytics/api-errors?${searchParams}`);
  }

  async getRevenueTrend() {
    return this.request<unknown[]>('/analytics/revenue-trend');
  }

  async getTicketTrend() {
    return this.request<unknown[]>('/analytics/ticket-trend');
  }

  async getDbTables() {
    return this.request<unknown[]>('/analytics/db-tables');
  }

  // ============================================
  // Admin Users (System Admin -> Users tab)
  // ============================================

  async listUsers(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<AdminUser[]>(`/users?${q}`);
  }

  async getUser(id: string) {
    return this.request<AdminUser>(`/users/${id}`);
  }

  async createUser(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'manager' | 'user';
    permissions?: string[];
    dashboardAccess?: string[];
    isActive?: boolean;
  }) {
    return this.request<AdminUser>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: Partial<{
    firstName: string;
    lastName: string;
    email: string;
    role: 'admin' | 'manager' | 'user';
    permissions: string[];
    dashboardAccess: string[];
    isActive: boolean;
  }>) {
    return this.request<AdminUser>(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deactivateUser(id: string) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  async revokeUserSessions(id: string) {
    return this.request<{ sessionsRevoked: number }>(`/users/${id}/revoke-sessions`, {
      method: 'POST',
    });
  }

  async getUserActivity(id: string, params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<AuditLogEntry[]>(`/users/${id}/activity?${q}`);
  }

  async getAuditLog(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    action?: string;
    tableName?: string;
  }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.userId) q.set('userId', params.userId);
    if (params?.action) q.set('action', params.action);
    if (params?.tableName) q.set('tableName', params.tableName);
    return this.request<AuditLogEntry[]>(`/users/audit-log?${q}`);
  }

  async getLoginEvents(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    eventType?: 'login_success' | 'login_failed' | 'logout' | 'token_refresh';
  }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.userId) q.set('userId', params.userId);
    if (params?.eventType) q.set('eventType', params.eventType);
    return this.request<LoginEvent[]>(`/users/login-events?${q}`);
  }

  async getLoginActivity() {
    return this.request<LoginActivityRow[]>('/users/login-activity');
  }

  async getOnlineUsers() {
    return this.request<OnlineUser[]>('/users/online');
  }

  // ============================================
  // Help Desk (admin management)
  // ============================================

  async getHelpdeskStats() {
    return this.request<unknown>('/helpdesk/stats');
  }

  async getHelpdeskWidgetTickets(params?: { page?: number; limit?: number; status?: string; priority?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.priority) searchParams.set('priority', params.priority);
    return this.request<unknown[]>(`/helpdesk/public-tickets?${searchParams}`);
  }

  async updateHelpdeskTicket(id: string, data: Record<string, unknown>) {
    return this.request(`/helpdesk/public-tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getWidgetKeys() {
    return this.request<unknown[]>('/helpdesk/widget-keys');
  }

  async createWidgetKey(data: { name: string; allowedDomains: string[] }) {
    return this.request('/helpdesk/widget-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeWidgetKey(id: string) {
    return this.request(`/helpdesk/widget-keys/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Lookup Values (Admin Configurable Drop-Downs)
  // ============================================

  async getLookupTypes() {
    return this.request<{ lookupType: string; valueCount: number; activeCount: number }[]>('/lookup-values/types');
  }

  async getLookupValues(params?: { lookupType?: string; parentId?: string; isActive?: boolean; search?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.lookupType) searchParams.set('lookupType', params.lookupType);
    if (params?.parentId) searchParams.set('parentId', params.parentId);
    if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/lookup-values?${searchParams}`);
  }

  async createLookupValue(data: Record<string, unknown>) {
    return this.request('/lookup-values', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateLookupValue(id: string, data: Record<string, unknown>) {
    return this.request(`/lookup-values/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteLookupValue(id: string) {
    return this.request(`/lookup-values/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Documents
  // ============================================

  async getDocuments(params?: {
    accountId?: string; contactId?: string; dealId?: string; ticketId?: string;
    invoiceId?: string; contractId?: string; expenseId?: string;
    category?: string; search?: string;
    page?: number; limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.contactId) searchParams.set('contactId', params.contactId);
    if (params?.dealId) searchParams.set('dealId', params.dealId);
    if (params?.ticketId) searchParams.set('ticketId', params.ticketId);
    if (params?.invoiceId) searchParams.set('invoiceId', params.invoiceId);
    if (params?.contractId) searchParams.set('contractId', params.contractId);
    if (params?.expenseId) searchParams.set('expenseId', params.expenseId);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/documents?${searchParams}`);
  }

  async getExpenseDocuments(expenseId: string) {
    return this.request<Array<{
      id: string;
      expenseId: string | null;
      fileName: string;
      originalName: string;
      mimeType: string;
      fileSize: number;
      filePath: string;
      category: string | null;
      description: string | null;
      uploadedBy: string;
      uploaderName: string;
      createdAt: string;
      updatedAt: string;
    }>>(`/expenses/${expenseId}/documents`);
  }

  getExpenseAuditReportUrl(params: {
    dateFrom: string;
    dateTo: string;
    businessProfileId?: string;
    categoryId?: string;
  }) {
    const q = new URLSearchParams();
    q.set('dateFrom', params.dateFrom);
    q.set('dateTo', params.dateTo);
    if (params.businessProfileId) q.set('businessProfileId', params.businessProfileId);
    if (params.categoryId) q.set('categoryId', params.categoryId);
    return `${API_URL}/expenses/audit-report?${q.toString()}`;
  }

  async downloadExpenseAuditReport(params: {
    dateFrom: string;
    dateTo: string;
    businessProfileId?: string;
    categoryId?: string;
  }) {
    const url = this.getExpenseAuditReportUrl(params);
    const headers: Record<string, string> = {};
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    const response = await fetch(url, { headers });
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed && this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        const retry = await fetch(url, { headers });
        if (!retry.ok) throw new Error(`Audit report failed (${retry.status})`);
        return retry.blob();
      }
    }
    if (!response.ok) throw new Error(`Audit report failed (${response.status})`);
    return response.blob();
  }

  async getDocument(id: string) {
    return this.request(`/documents/${id}`);
  }

  async uploadDocument(file: File, metadata: Record<string, string | undefined>) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(metadata).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });
    return this.uploadRequest('/documents', formData);
  }

  async updateDocument(id: string, data: Record<string, unknown>) {
    return this.request(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteDocument(id: string) {
    return this.request(`/documents/${id}`, { method: 'DELETE' });
  }

  async uploadDocumentVersion(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.uploadRequest(`/documents/${id}/version`, formData);
  }

  async getDocumentVersions(id: string) {
    return this.request<unknown[]>(`/documents/${id}/versions`);
  }

  getDocumentDownloadUrl(id: string) {
    return `${API_URL}/documents/${id}/download`;
  }

  // ============================================
  // Activities
  // ============================================

  async getActivities(params?: {
    accountId?: string; contactId?: string; dealId?: string; ticketId?: string;
    invoiceId?: string; contractId?: string; activityType?: string; category?: string;
    dateFrom?: string; dateTo?: string; performedBy?: string; search?: string;
    page?: number; limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.contactId) searchParams.set('contactId', params.contactId);
    if (params?.dealId) searchParams.set('dealId', params.dealId);
    if (params?.ticketId) searchParams.set('ticketId', params.ticketId);
    if (params?.invoiceId) searchParams.set('invoiceId', params.invoiceId);
    if (params?.contractId) searchParams.set('contractId', params.contractId);
    if (params?.activityType) searchParams.set('activityType', params.activityType);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.performedBy) searchParams.set('performedBy', params.performedBy);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/activities?${searchParams}`);
  }

  async createActivity(data: Record<string, unknown>) {
    return this.request('/activities', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateActivity(id: string, data: Record<string, unknown>) {
    return this.request(`/activities/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteActivity(id: string) {
    return this.request(`/activities/${id}`, { method: 'DELETE' });
  }

  async getActivitySummary(params?: { dateFrom?: string; dateTo?: string; accountId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    return this.request<{ activityType: string; activityCount: number; totalDurationMinutes: number }[]>(
      `/activities/summary?${searchParams}`
    );
  }

  // ============================================
  // Calendar
  // ============================================

  async getCalendarEvents(params: { startDate: string; endDate: string; eventType?: string }) {
    const searchParams = new URLSearchParams();
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    if (params.eventType) searchParams.set('eventType', params.eventType);
    return this.request<unknown[]>(`/calendar/events?${searchParams}`);
  }

  // ============================================
  // Approval Workflows
  // ============================================

  async getApprovalChains(params?: { entityType?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.entityType) searchParams.set('entityType', params.entityType);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/approvals/chains?${searchParams}`);
  }

  async getApprovalChain(id: string) {
    return this.request(`/approvals/chains/${id}`);
  }

  async createApprovalChain(data: Record<string, unknown>) {
    return this.request('/approvals/chains', { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteApprovalChain(id: string) {
    return this.request(`/approvals/chains/${id}`, { method: 'DELETE' });
  }

  async submitApprovalRequest(data: { chainId: string; entityType: string; entityId: string }) {
    return this.request('/approvals/requests', { method: 'POST', body: JSON.stringify(data) });
  }

  async getApprovalRequests(params?: { entityType?: string; entityId?: string; status?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.entityType) searchParams.set('entityType', params.entityType);
    if (params?.entityId) searchParams.set('entityId', params.entityId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/approvals/requests?${searchParams}`);
  }

  async getApprovalRequest(id: string) {
    return this.request(`/approvals/requests/${id}`);
  }

  async actOnApprovalRequest(id: string, data: { action: string; comment?: string }) {
    return this.request(`/approvals/requests/${id}/action`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getMyPendingApprovals(params?: { page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/approvals/my-pending?${searchParams}`);
  }

  // ============================================
  // Report Builder
  // ============================================

  async getSavedReports(params?: { search?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/reports?${searchParams}`);
  }

  async getSavedReport(id: string) {
    return this.request(`/reports/${id}`);
  }

  async saveReport(data: Record<string, unknown>) {
    return this.request('/reports', { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteReport(id: string) {
    return this.request(`/reports/${id}`, { method: 'DELETE' });
  }

  async executeReport(data: { dataSource: string; filters: Record<string, unknown> }) {
    return this.request<unknown[]>('/reports/execute', { method: 'POST', body: JSON.stringify(data) });
  }

  // ============================================
  // Scheduled Reports
  // ============================================

  async getScheduledReports(params?: { page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/scheduled-reports?${searchParams}`);
  }

  async createScheduledReport(data: Record<string, unknown>) {
    return this.request('/scheduled-reports', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateScheduledReport(id: string, data: Record<string, unknown>) {
    return this.request(`/scheduled-reports/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteScheduledReport(id: string) {
    return this.request(`/scheduled-reports/${id}`, { method: 'DELETE' });
  }

  async runScheduledReportNow(id: string) {
    return this.request(`/scheduled-reports/${id}/run-now`, { method: 'POST' });
  }

  async getScheduledReportLogs(id: string, params?: { page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/scheduled-reports/${id}/logs?${searchParams}`);
  }

  // ============================================
  // Document Search & Preview
  // ============================================

  async searchDocuments(params: { q: string; accountId?: string; category?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);
    if (params.accountId) searchParams.set('accountId', params.accountId);
    if (params.category) searchParams.set('category', params.category);
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/documents/search?${searchParams}`);
  }

  getDocumentPreviewUrl(id: string) {
    return `${API_URL}/documents/${id}/preview`;
  }

  // ============================================
  // Outlook Calendar Sync
  // ============================================

  async getOutlookStatus() {
    return this.request<{ connected: boolean; expiresAt?: string }>('/outlook/status');
  }

  async getOutlookAuthUrl() {
    return this.request<{ authUrl: string }>('/outlook/auth-url');
  }

  async disconnectOutlook() {
    return this.request('/outlook/disconnect', { method: 'DELETE' });
  }

  async getSyncedEvents(params?: { page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/outlook/synced-events?${searchParams}`);
  }

  async syncEventToOutlook(data: { entityType: string; entityId: string }) {
    return this.request('/outlook/sync-event', { method: 'POST', body: JSON.stringify(data) });
  }

  async syncPaymentTasksToOutlook(data: { weekStart?: string; businessProfileId?: string }) {
    return this.request<{ synced: number; total: number; tasks: Array<{ paymentName: string; taskId: string }> }>(
      '/outlook/sync-payment-tasks',
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  // ============================================
  // Tax & Compliance
  // ============================================

  async getTaxSummary(params: { taxYear: number; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    searchParams.set('taxYear', params.taxYear.toString());
    if (params.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/tax/summary?${searchParams}`);
  }

  async getTaxYears(params?: { businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/tax/years?${searchParams}`);
  }

  async createTaxYear(data: Record<string, unknown>) {
    return this.request('/tax/years', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTaxYear(id: string, data: Record<string, unknown>) {
    return this.request(`/tax/years/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteTaxYear(id: string) {
    return this.request(`/tax/years/${id}`, { method: 'DELETE' });
  }

  async getEstimatedPayments(taxYearId: string) {
    return this.request<unknown[]>(`/tax/estimated-payments/${taxYearId}`);
  }

  async updateEstimatedPayment(id: string, data: Record<string, unknown>) {
    return this.request(`/tax/estimated-payments/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async getTaxDeductions(params: { taxYear: number; taxCategory?: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    searchParams.set('taxYear', params.taxYear.toString());
    if (params.taxCategory) searchParams.set('taxCategory', params.taxCategory);
    if (params.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/tax/deductions?${searchParams}`);
  }

  async getScheduleC(params: { taxYear: number; businessProfileId: string }) {
    const searchParams = new URLSearchParams();
    searchParams.set('taxYear', params.taxYear.toString());
    searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/tax/schedule-c?${searchParams}`);
  }

  async calculateTax(data: Record<string, unknown>) {
    return this.request('/tax/calculate', { method: 'POST', body: JSON.stringify(data) });
  }

  async calculateTaxAuto(params: { taxYear: number; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    searchParams.set('taxYear', params.taxYear.toString());
    if (params.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/tax/calculate/auto?${searchParams}`);
  }

  async getTaxBrackets(params?: { filingStatus?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.filingStatus) searchParams.set('filingStatus', params.filingStatus);
    return this.request<unknown>(`/tax/brackets?${searchParams}`);
  }

  // Tax Scenarios
  async listTaxScenarios(params?: { taxYear?: number; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.taxYear) searchParams.set('taxYear', params.taxYear.toString());
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/tax/scenarios?${searchParams}`);
  }

  async getTaxScenario(id: string) {
    return this.request<unknown>(`/tax/scenarios/${id}`);
  }

  async createTaxScenario(data: Record<string, unknown>) {
    return this.request('/tax/scenarios', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTaxScenario(id: string, data: Record<string, unknown>) {
    return this.request(`/tax/scenarios/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteTaxScenario(id: string) {
    return this.request(`/tax/scenarios/${id}`, { method: 'DELETE' });
  }

  async getTaxAuditLog(params?: { tableName?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.tableName) searchParams.set('tableName', params.tableName);
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/tax/audit-log?${searchParams}`);
  }

  // ============================================
  // Financial Statements
  // ============================================

  async getIncomeStatement(params?: { dateFrom?: string; dateTo?: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/financial-statements/income?${searchParams}`);
  }

  async getBalanceSheet(params?: { asOfDate?: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.asOfDate) searchParams.set('asOfDate', params.asOfDate);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/financial-statements/balance-sheet?${searchParams}`);
  }

  async getCashFlowStatement(params?: { dateFrom?: string; dateTo?: string; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/financial-statements/cash-flow?${searchParams}`);
  }

  // ============================================
  // Forecasts
  // ============================================

  async getForecastHistory(params?: { yearsBack?: number; businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.yearsBack) searchParams.set('yearsBack', params.yearsBack.toString());
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown>(`/forecasts/history?${searchParams}`);
  }

  async listForecasts(params?: { businessProfileId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.businessProfileId) searchParams.set('businessProfileId', params.businessProfileId);
    return this.request<unknown[]>(`/forecasts?${searchParams}`);
  }

  async getForecast(id: string) {
    return this.request<unknown>(`/forecasts/${id}`);
  }

  async createForecast(data: Record<string, unknown>) {
    return this.request('/forecasts', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateForecast(id: string, data: Record<string, unknown>) {
    return this.request(`/forecasts/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteForecast(id: string) {
    return this.request(`/forecasts/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Project Management
  // ============================================

  async getProjectDashboard() {
    return this.request<unknown>('/projects/dashboard');
  }

  async listProjects(params?: { status?: string; projectType?: string; accountId?: string; projectManagerId?: string; search?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.projectType) searchParams.set('projectType', params.projectType);
    if (params?.accountId) searchParams.set('accountId', params.accountId);
    if (params?.projectManagerId) searchParams.set('projectManagerId', params.projectManagerId);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/projects?${searchParams}`);
  }

  async getProject(id: string) {
    return this.request<unknown>(`/projects/${id}`);
  }

  async createProject(data: Record<string, unknown>) {
    return this.request<unknown>('/projects', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateProject(id: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteProject(id: string) {
    return this.request<unknown>(`/projects/${id}`, { method: 'DELETE' });
  }

  // Phases
  async listPhases(projectId: string) {
    return this.request<unknown[]>(`/projects/${projectId}/phases`);
  }

  async createPhase(projectId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/${projectId}/phases`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePhase(phaseId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/phases/${phaseId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deletePhase(phaseId: string) {
    return this.request<unknown>(`/projects/phases/${phaseId}`, { method: 'DELETE' });
  }

  // Tasks
  async listProjectTasks(params?: { projectId?: string; phaseId?: string; status?: string; assigneeId?: string; search?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.set('projectId', params.projectId);
    if (params?.phaseId) searchParams.set('phaseId', params.phaseId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.assigneeId) searchParams.set('assigneeId', params.assigneeId);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/projects/tasks/list?${searchParams}`);
  }

  async getTaskBoard(projectId: string) {
    return this.request<unknown>(`/projects/${projectId}/tasks/board`);
  }

  async getProjectTask(taskId: string) {
    return this.request<unknown>(`/projects/tasks/${taskId}`);
  }

  async createProjectTask(data: Record<string, unknown>) {
    return this.request<unknown>('/projects/tasks', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateProjectTask(taskId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteProjectTask(taskId: string) {
    return this.request<unknown>(`/projects/tasks/${taskId}`, { method: 'DELETE' });
  }

  // Time Entries
  async listTimeEntries(params?: { projectId?: string; taskId?: string; userId?: string; dateFrom?: string; dateTo?: string; isBillable?: boolean; isInvoiced?: boolean; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.set('projectId', params.projectId);
    if (params?.taskId) searchParams.set('taskId', params.taskId);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.isBillable !== undefined) searchParams.set('isBillable', params.isBillable.toString());
    if (params?.isInvoiced !== undefined) searchParams.set('isInvoiced', params.isInvoiced.toString());
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/projects/time-entries/list?${searchParams}`);
  }

  async getTimesheet(weekStart: string, userId?: string) {
    const searchParams = new URLSearchParams({ weekStart });
    if (userId) searchParams.set('userId', userId);
    return this.request<unknown[]>(`/projects/time-entries/timesheet?${searchParams}`);
  }

  async createTimeEntry(data: Record<string, unknown>) {
    return this.request<unknown>('/projects/time-entries', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTimeEntry(entryId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/time-entries/${entryId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteTimeEntry(entryId: string) {
    return this.request<unknown>(`/projects/time-entries/${entryId}`, { method: 'DELETE' });
  }

  // Requirements
  async listRequirements(params?: { projectId?: string; category?: string; status?: string; search?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.set('projectId', params.projectId);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/projects/requirements/list?${searchParams}`);
  }

  async getTraceabilityMatrix(projectId: string) {
    return this.request<unknown[]>(`/projects/${projectId}/traceability-matrix`);
  }

  async createRequirement(data: Record<string, unknown>) {
    return this.request<unknown>('/projects/requirements', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateRequirement(reqId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/requirements/${reqId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteRequirement(reqId: string) {
    return this.request<unknown>(`/projects/requirements/${reqId}`, { method: 'DELETE' });
  }

  async createRequirementLink(data: Record<string, unknown>) {
    return this.request<unknown>('/projects/requirements/links', { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteRequirementLink(linkId: string) {
    return this.request<unknown>(`/projects/requirements/links/${linkId}`, { method: 'DELETE' });
  }

  // Risks
  async listRisks(params?: { projectId?: string; status?: string; category?: string; search?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.set('projectId', params.projectId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/projects/risks/list?${searchParams}`);
  }

  async getRisk(riskId: string) {
    return this.request<unknown>(`/projects/risks/${riskId}`);
  }

  async createRisk(data: Record<string, unknown>) {
    return this.request<unknown>('/projects/risks', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateRisk(riskId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/risks/${riskId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteRisk(riskId: string) {
    return this.request<unknown>(`/projects/risks/${riskId}`, { method: 'DELETE' });
  }

  // Deliverables
  async listDeliverables(params?: { projectId?: string; phaseId?: string; status?: string; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.set('projectId', params.projectId);
    if (params?.phaseId) searchParams.set('phaseId', params.phaseId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return this.request<unknown[]>(`/projects/deliverables/list?${searchParams}`);
  }

  async createDeliverable(data: Record<string, unknown>) {
    return this.request<unknown>('/projects/deliverables', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateDeliverable(deliverableId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/deliverables/${deliverableId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteDeliverable(deliverableId: string) {
    return this.request<unknown>(`/projects/deliverables/${deliverableId}`, { method: 'DELETE' });
  }

  // Resource Assignments
  async listResourceAssignments(projectId: string) {
    return this.request<unknown[]>(`/projects/${projectId}/resources`);
  }

  async createResourceAssignment(projectId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/${projectId}/resources`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateResourceAssignment(assignmentId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/projects/resources/${assignmentId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteResourceAssignment(assignmentId: string) {
    return this.request<unknown>(`/projects/resources/${assignmentId}`, { method: 'DELETE' });
  }

  // ============================================
  // Helpdesk: SLA Policies
  // ============================================

  async getSlaPolicies(params?: { applicationId?: string; isActive?: boolean; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    if (params?.isActive !== undefined) q.set('isActive', String(params.isActive));
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<unknown[]>(`/sla?${q}`);
  }

  async getSlaPolicy(id: string) {
    return this.request<unknown>(`/sla/${id}`);
  }

  async createSlaPolicy(data: Record<string, unknown>) {
    return this.request<unknown>('/sla', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateSlaPolicy(id: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/sla/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteSlaPolicy(id: string) {
    return this.request<unknown>(`/sla/${id}`, { method: 'DELETE' });
  }

  async getEscalationRules(policyId: string) {
    return this.request<unknown[]>(`/sla/${policyId}/escalations`);
  }

  async createEscalationRule(policyId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/sla/${policyId}/escalations`, { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteEscalationRule(ruleId: string) {
    return this.request<unknown>(`/sla/escalations/${ruleId}`, { method: 'DELETE' });
  }

  // ============================================
  // Helpdesk: Notifications
  // ============================================

  async getNotificationPrefs() {
    return this.request<unknown>('/notifications/preferences');
  }

  async updateNotificationPrefs(data: Record<string, unknown>) {
    return this.request<unknown>('/notifications/preferences', { method: 'PATCH', body: JSON.stringify(data) });
  }

  async getNotificationLog(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<unknown[]>(`/notifications/log?${q}`);
  }

  async markNotificationRead(id: string) {
    return this.request<unknown>(`/notifications/log/${id}/read`, { method: 'PATCH' });
  }

  // ============================================
  // Helpdesk: Email Templates
  // ============================================

  async getEmailTemplates() {
    return this.request<unknown[]>('/email-templates');
  }

  async getEmailTemplate(key: string) {
    return this.request<unknown>(`/email-templates/${key}`);
  }

  async upsertEmailTemplate(key: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/email-templates/${key}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // ============================================
  // Helpdesk: Canned Responses
  // ============================================

  async getCannedResponses(params?: { search?: string; category?: string; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.category) q.set('category', params.category);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<unknown[]>(`/canned-responses?${q}`);
  }

  async createCannedResponse(data: Record<string, unknown>) {
    return this.request<unknown>('/canned-responses', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateCannedResponse(id: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/canned-responses/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteCannedResponse(id: string) {
    return this.request<unknown>(`/canned-responses/${id}`, { method: 'DELETE' });
  }

  async useCannedResponse(id: string) {
    return this.request<unknown>(`/canned-responses/${id}/use`, { method: 'POST' });
  }

  // ============================================
  // Helpdesk: Knowledge Base
  // ============================================

  async getKbCategories() {
    return this.request<unknown[]>('/knowledge-base/categories');
  }

  async createKbCategory(data: Record<string, unknown>) {
    return this.request<unknown>('/knowledge-base/categories', { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteKbCategory(id: string) {
    return this.request<unknown>(`/knowledge-base/categories/${id}`, { method: 'DELETE' });
  }

  async getKbArticles(params?: { search?: string; categoryId?: string; applicationId?: string; status?: string; isPublic?: boolean; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.categoryId) q.set('categoryId', params.categoryId);
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    if (params?.status) q.set('status', params.status);
    if (params?.isPublic !== undefined) q.set('isPublic', String(params.isPublic));
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<unknown[]>(`/knowledge-base/articles?${q}`);
  }

  async getKbArticle(id: string) {
    return this.request<unknown>(`/knowledge-base/articles/${id}`);
  }

  async createKbArticle(data: Record<string, unknown>) {
    return this.request<unknown>('/knowledge-base/articles', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateKbArticle(id: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/knowledge-base/articles/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteKbArticle(id: string) {
    return this.request<unknown>(`/knowledge-base/articles/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Helpdesk: Surveys (CSAT)
  // ============================================

  async getSurveyByToken(token: string) {
    return this.request<unknown>(`/surveys/respond/${token}`);
  }

  async submitSurvey(token: string, data: { rating: number; comment?: string }) {
    return this.request<unknown>(`/surveys/respond/${token}`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getSurveyStats(params?: { applicationId?: string; dateFrom?: string; dateTo?: string }) {
    const q = new URLSearchParams();
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    if (params?.dateFrom) q.set('dateFrom', params.dateFrom);
    if (params?.dateTo) q.set('dateTo', params.dateTo);
    return this.request<unknown>(`/surveys/stats?${q}`);
  }

  // ============================================
  // Helpdesk: Ticket Links & Watchers
  // ============================================

  async getTicketLinks(ticketId: string) {
    return this.request<unknown[]>(`/ticket-links/ticket/${ticketId}`);
  }

  async createTicketLink(ticketId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/ticket-links/ticket/${ticketId}`, { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteTicketLink(linkId: string) {
    return this.request<unknown>(`/ticket-links/${linkId}`, { method: 'DELETE' });
  }

  async mergeTickets(sourceTicketId: string, data: { targetTicketId: string }) {
    return this.request<unknown>(`/ticket-links/ticket/${sourceTicketId}/merge`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getTicketWatchers(ticketId: string) {
    return this.request<unknown[]>(`/ticket-watchers/ticket/${ticketId}`);
  }

  async addTicketWatcher(ticketId: string, userId: string) {
    return this.request<unknown>(`/ticket-watchers/ticket/${ticketId}`, { method: 'POST', body: JSON.stringify({ userId }) });
  }

  async removeTicketWatcher(ticketId: string, userId: string) {
    return this.request<unknown>(`/ticket-watchers/ticket/${ticketId}/user/${userId}`, { method: 'DELETE' });
  }

  // ============================================
  // Helpdesk: App Status & Incidents
  // ============================================

  async getAppStatuses() {
    return this.request<unknown[]>('/app-status');
  }

  async getAppStatus(applicationId: string) {
    return this.request<unknown>(`/app-status/${applicationId}`);
  }

  async updateAppStatus(applicationId: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/app-status/${applicationId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async getIncidents(applicationId: string) {
    return this.request<unknown[]>(`/app-status/${applicationId}/incidents`);
  }

  async createIncident(data: Record<string, unknown>) {
    return this.request<unknown>('/app-status/incidents', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateIncident(id: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/app-status/incidents/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteIncident(id: string) {
    return this.request<unknown>(`/app-status/incidents/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Helpdesk: Automation Rules
  // ============================================

  async getAutomationRules(params?: { triggerEvent?: string; isActive?: boolean; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.triggerEvent) q.set('triggerEvent', params.triggerEvent);
    if (params?.isActive !== undefined) q.set('isActive', String(params.isActive));
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<unknown[]>(`/automation-rules?${q}`);
  }

  async getAutomationRule(id: string) {
    return this.request<unknown>(`/automation-rules/${id}`);
  }

  async createAutomationRule(data: Record<string, unknown>) {
    return this.request<unknown>('/automation-rules', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateAutomationRule(id: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/automation-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteAutomationRule(id: string) {
    return this.request<unknown>(`/automation-rules/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Helpdesk: Maintenance Windows
  // ============================================

  async getMaintenanceWindows(params?: { applicationId?: string; status?: string; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    if (params?.status) q.set('status', params.status);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    return this.request<unknown[]>(`/maintenance?${q}`);
  }

  async getUpcomingMaintenance(applicationId?: string) {
    const q = applicationId ? `?applicationId=${applicationId}` : '';
    return this.request<unknown[]>(`/maintenance/upcoming${q}`);
  }

  async createMaintenanceWindow(data: Record<string, unknown>) {
    return this.request<unknown>('/maintenance', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateMaintenanceWindow(id: string, data: Record<string, unknown>) {
    return this.request<unknown>(`/maintenance/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteMaintenanceWindow(id: string) {
    return this.request<unknown>(`/maintenance/${id}`, { method: 'DELETE' });
  }

  // ============================================
  // Helpdesk: Reports
  // ============================================

  async getHelpdeskSummary(params?: { period?: string; applicationId?: string }) {
    const q = new URLSearchParams();
    if (params?.period) q.set('period', params.period);
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    return this.request<unknown>(`/helpdesk-reports/summary?${q}`);
  }

  async getAgentPerformance(params?: { period?: string; applicationId?: string }) {
    const q = new URLSearchParams();
    if (params?.period) q.set('period', params.period);
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    return this.request<unknown[]>(`/helpdesk-reports/agent-performance?${q}`);
  }

  async getSlaCompliance(params?: { period?: string; applicationId?: string }) {
    const q = new URLSearchParams();
    if (params?.period) q.set('period', params.period);
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    return this.request<unknown[]>(`/helpdesk-reports/sla-compliance?${q}`);
  }

  async getChannelBreakdown(params?: { period?: string }) {
    const q = new URLSearchParams();
    if (params?.period) q.set('period', params.period);
    return this.request<unknown[]>(`/helpdesk-reports/channel-breakdown?${q}`);
  }

  async getTicketTrends(params?: { period?: string; granularity?: string; applicationId?: string }) {
    const q = new URLSearchParams();
    if (params?.period) q.set('period', params.period);
    if (params?.granularity) q.set('granularity', params.granularity);
    if (params?.applicationId) q.set('applicationId', params.applicationId);
    return this.request<unknown[]>(`/helpdesk-reports/trends?${q}`);
  }
}

export const api = new ApiClient();
